import React, { useState, useMemo } from "react";

import { useSyncGlobalPageLoader } from "../../../contexts/global-page-loader-context";
import { Link, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import UsersList from "./users-list";
import useGetApi from "../../../hooks/use-get-api";
import endpoints, { IMember } from "../../../api/endpoints";
import { RootState } from "../../../redux/store";
import { canAccess } from "../../../utils/permissions";

// ----------------------------------------------------------

enum UsersTab {
  ACTIVE_USERS = "Active",
  DEACTIVE_USERS = "Inactive",
}

interface Props {
  users_type?: "all" | "active" | "inactive" | "both";
}

type BalanceStatResponse = Partial<{
  available_credit_limit: number;
  balance: number;
  credit_reference: number;
  admin_upline: number;
  admin_downline: number;
  total_downline_balance: number;
  total_downline_credit_reference: number;
}>;

// ----------------------------------------------------------

const ListClientsView: React.FC<Props> = ({ users_type }) => {
  const [selectedTab, setSelectedTab] = useState<string>(UsersTab.ACTIVE_USERS);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [showBalance, setShowBalance] = useState(false);

  const { userId } = useParams();
  const { pathname } = useLocation();
  const userData = useSelector((state: RootState) => state.user.userData);
  const loggedInAdminId = userData?.user_id ?? "";
  const canInsertUser = canAccess(userData, "insert_user");

  const isListClients = pathname.split("/")[1] === "list-clients";

  const offset = (currentPage - 1) * pageSize;

  const listStatus =
    users_type === "all"
      ? selectedTab === UsersTab.ACTIVE_USERS
        ? "active"
        : "inactive"
      : "both";

  const listEndpoint = endpoints.member.allpanelDownlineList(
    userId as string,
    pageSize,
    offset,
    listStatus,
    "both",
    appliedSearch
  );

  const { data, isLoading } = useGetApi<any>(listEndpoint, Boolean(userId));

  useSyncGlobalPageLoader(isLoading);

  const accountList = data?.downLine ?? [];
  const totalRecords: number = data?.count ?? accountList.length;

  const { data: balanceStat } = useGetApi<BalanceStatResponse>(
    endpoints.member.allpanelStatistics(loggedInAdminId),
    !!loggedInAdminId
  );

  const masterBalance = useMemo(() => {
    const upperCreditRef = balanceStat?.credit_reference ?? 0;
    const availableBalance = balanceStat?.balance ?? 0;
    const totalBalance = balanceStat?.available_credit_limit ?? 0;
    const downLineOccupyBalance = balanceStat?.total_downline_balance ?? 0;
    const downLineCreditRef = balanceStat?.total_downline_credit_reference ?? 0;
    const myPnl = 0;

    return {
      upperCreditRef,
      downLineOccupyBalance,
      downLineCreditRef,
      totalBalance,
      upperLevel: upperCreditRef - totalBalance,
      downLinePnl: downLineOccupyBalance - downLineCreditRef,
      availableBalance,
      availableBalanceWithPnl: availableBalance - myPnl,
      myPnl,
    };
  }, [balanceStat]);

  const handleLoad = () => {
    setAppliedSearch(searchText);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchText("");
    setAppliedSearch("");
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    setCurrentPage(1);
  };

  const tabsContent = users_type === "all" ? (
    <ul className="nav nav-tabs">
      <li className="nav-item">
        <a
          href="#"
          className={`nav-link ${selectedTab === UsersTab.ACTIVE_USERS ? "active tab-bg-warning" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            handleTabChange(UsersTab.ACTIVE_USERS);
          }}
        >
          Active Users
        </a>
      </li>
      <li className="nav-item">
        <a
          href="#"
          className={`nav-link ${selectedTab === UsersTab.DEACTIVE_USERS ? "active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            handleTabChange(UsersTab.DEACTIVE_USERS);
          }}
        >
          Deactive Users
        </a>
      </li>
    </ul>
  ) : undefined;

  return (
    <div className="listing-grid">
      {users_type === "all" && (
        <div className="master-balance">
          <div className="text-center">
            <span
              id="user-balance"
              className={`far fa-arrow-alt-circle-${showBalance ? "up" : "down"}`}
              style={{ cursor: "pointer" }}
              role="button"
              tabIndex={0}
              aria-expanded={showBalance}
              onClick={() => setShowBalance((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowBalance((prev) => !prev);
                }
              }}
            />
          </div>
          {/* eslint-disable jsx-a11y/label-has-associated-control */}
          {showBalance && (
            <div id="master-balance-detail" className="master-balance-detail mt-3">
              <ul className="row">
                <li className="col-md-4">
                  <label className="col-md-8 text-left">Upper Level Credit Referance:</label>
                  <span className="text-right col-md-4">{masterBalance.upperCreditRef.toLocaleString("en-IN")}</span>
                </li>
                <li className="col-md-4">
                  <label className="col-md-8 text-left">Down level Occupy Balance:</label>
                  <span className="text-right col-md-4">{masterBalance.downLineOccupyBalance.toLocaleString("en-IN")}</span>
                </li>
                <li className="col-md-4">
                  <label className="col-md-8 text-left">Down Level Credit Referance:</label>
                  <span className="text-right col-md-4">{masterBalance.downLineCreditRef.toLocaleString("en-IN")}</span>
                </li>
                <li className="col-md-4">
                  <label className="col-md-8 text-left">Total Master Balance</label>
                  <span className="text-right col-md-4">{masterBalance.totalBalance.toLocaleString("en-IN")}</span>
                </li>
                <li className="col-md-4">
                  <label className="col-md-8 text-left">Upper Level:</label>
                  <span className="text-right col-md-4">{masterBalance.upperLevel.toLocaleString("en-IN")}</span>
                </li>
                <li className="col-md-4">
                  <label className="col-md-8 text-left">Down Level Profit/Loss :</label>
                  <span className="text-right col-md-4">{masterBalance.downLinePnl.toLocaleString("en-IN")}</span>
                </li>
                <li className="col-md-4">
                  <label className="col-md-8 text-left">Available Balance:</label>
                  <span className="text-right col-md-4">{masterBalance.availableBalance.toLocaleString("en-IN")}</span>
                </li>
                <li className="col-md-4">
                  <label className="col-md-8 text-left">Available Balance With Profit/Loss:</label>
                  <span className="text-right col-md-4">{masterBalance.availableBalanceWithPnl.toLocaleString("en-IN")}</span>
                </li>
                <li className="col-md-4">
                  <label className="col-md-8 text-left">My Profit/Loss:</label>
                  <span className="text-right col-md-4">{masterBalance.myPnl.toLocaleString("en-IN")}</span>
                </li>
              </ul>
            </div>
          )}
          {/* eslint-enable jsx-a11y/label-has-associated-control */}
        </div>
      )}

      <div>
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-flex align-items-center justify-content-between">
              <h4 className="mb-0 font-size-18">Account List</h4>
              <div className="page-title-right">
                {canInsertUser && (
                  <div className="d-inline-block">
                    <Link to="/add-account" className="btn btn-primary">
                      Add Account
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row account-list">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <UsersList
                  isListClients={isListClients}
                  accountList={accountList}
                  parentUserId={userId as string}
                  tabsContent={tabsContent}
                  searchText={searchText}
                  onSearchChange={setSearchText}
                  onLoad={handleLoad}
                  onReset={handleReset}
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  currentPage={currentPage}
                  totalRecords={totalRecords}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListClientsView;
